import { addCommasToNumber } from '@gravitate-js/excalibrr'
import type { ConfigField, ConfigType } from './configTypes'

// Reusable threshold value formatter
export function formatThresholdValue(value: number | undefined | null, unit: ConfigField['unit']): string {
  if (value === undefined || value === null) return ''

  const formattedValue = Number.parseFloat(value.toString()).toFixed(unit === '%' ? 2 : unit === 'hrs' ? 0 : 2)

  switch (unit) {
    case '%':
      return `${formattedValue}%`
    case '$':
      return `$${addCommasToNumber(Number(formattedValue))}`
    case 'hrs':
      return `${formattedValue}hrs`
    default:
      return value.toString()
  }
}

export function formatSwitchValue(value: number | null | undefined, unit: ConfigField['unit']) {
  if (value === null || value === undefined) return 'ON'

  switch (unit) {
    case '%':
      // Convert decimal to percentage and display appropriately
      const percentage = Math.round(value * 10000) / 100
      // If it's a whole number, don't show decimals
      if (percentage % 1 === 0) {
        return `${Math.round(percentage)}%`
      }
      // Otherwise show up to 2 decimal places
      return `${addCommasToNumber(percentage, 2)}%`
    case '$':
      return `$${addCommasToNumber(value, 0)}`
    case 'hrs':
      return `${value}hrs`
    default:
      return value.toString()
  }
}

export function getThresholdDescription(fieldKey: string, configType: ConfigType, field: ConfigField) {
  const baseDescription = (() => {
    switch (fieldKey) {
      case 'by_gross_volume':
        return 'Set the acceptable variance percentage for gross volume differences.'
      case 'by_net_volume':
        return 'Define the allowable difference in net volume as a percentage.'
      case 'by_total':
        return 'Specify the maximum dollar amount difference allowed between BOL and invoice totals.'
      case 'by_total_per_unit':
        return 'Set the acceptable variance in price per unit.'
      case 'by_product_total':
        return 'Define the allowed variance percentage in product totals, excluding taxes and fees.'
      case 'by_tax_total':
        return 'Set the acceptable difference percentage for tax totals between BOL and invoice.'
      case 'by_bol_date':
        return 'Specify the maximum time difference (in hours) between BOL and invoice dates.'
      default:
        return field.description || 'Set threshold value for this rule'
    }
  })()

  switch (configType) {
    case 'auto_match_rules':
      return `${baseDescription} When this threshold is exceeded, the system will not automatically match the BOL to the invoice.`
    case 'auto_approve_rules':
      return `${baseDescription} Matches within this threshold will be automatically approved.`
    case 'warning_rules':
      return `${baseDescription} The system will generate a warning when this threshold is exceeded.`
    default:
      return baseDescription
  }
}

export function getPopoverTitle(configType: ConfigType, field: ConfigField) {
  switch (configType) {
    case 'auto_match_rules':
      return `Filter Matches ${field.name}`
    case 'auto_approve_rules':
      return `Auto Approve ${field.name}`
    case 'warning_rules':
      return `Add Warnings ${field.name}`
    default:
      return `Configure ${field.name}`
  }
}
